import { ref } from 'vue';

/**
 * GA4Core: Base GA4 layer composable
 *
 * Provides core GA4 tracking methods with:
 * - DTO validation
 * - Backend guard for purchase tracking
 * - In-memory duplicate detection
 * - Comprehensive logging
 *
 * Usage:
 *   const { trackViewItemList, trackViewItem, trackBeginCheckout, trackAddPaymentInfo, trackPurchase } = useGA4Core(store, getVuexAlias);
 */

const ensureDataLayer = () => {
    if (typeof window !== 'undefined') {
        window.dataLayer = window.dataLayer || [];
    }
};

const pushEcommerceEvent = (eventName, ecommerce = {}) => {
    if (typeof window === 'undefined') {
        return;
    }

    ensureDataLayer();
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
        event: eventName,
        ecommerce,
    });
};

/**
 * Validate DTO structure
 * Returns { valid: boolean, errors: string[] }
 */
const validateDTO = (dto, eventType) => {
    const errors = [];

    if (!dto || typeof dto !== 'object') {
        errors.push('[GA4Core] DTO must be an object');
        return { valid: false, errors };
    }

    // All events require: value, currency, items[]
    if (typeof dto.value !== 'number' || dto.value < 0) {
        errors.push('[GA4Core] DTO.value must be a non-negative number');
    }

    if (!dto.currency || typeof dto.currency !== 'string') {
        errors.push('[GA4Core] DTO.currency must be a non-empty string');
    }

    if (!Array.isArray(dto.items)) {
        errors.push('[GA4Core] DTO.items must be an array');
    } else if (dto.items.length === 0) {
        errors.push('[GA4Core] DTO.items must contain at least one item');
    } else {
        // Validate each item in the array
        dto.items.forEach((item, idx) => {
            if (!item.item_id || typeof item.item_id !== 'string') {
                errors.push(`[GA4Core] DTO.items[${idx}].item_id must be a non-empty string`);
            }
            if (!item.item_name || typeof item.item_name !== 'string') {
                errors.push(`[GA4Core] DTO.items[${idx}].item_name must be a non-empty string`);
            }
            if (typeof item.price !== 'number' || item.price < 0) {
                errors.push(`[GA4Core] DTO.items[${idx}].price must be a non-negative number`);
            }
            if (typeof item.quantity !== 'number' || item.quantity < 1) {
                errors.push(`[GA4Core] DTO.items[${idx}].quantity must be a positive number`);
            }
        });
    }

    // Purchase-specific validation
    if (eventType === 'purchase') {
        if (!dto.transactionId || typeof dto.transactionId !== 'string') {
            errors.push('[GA4Core] DTO.transactionId is required for purchase events and must be a non-empty string');
        }
    }

    return {
        valid: errors.length === 0,
        errors,
    };
};

export function useGA4Core(store, getVuexAlias) {
    // In-memory guard: track transactionIds that have been pushed
    const trackedTransactionIds = ref(new Set());

    /**
     * trackViewItemList(dto)
     * Push view_item_list event to GA4
     */
    const trackViewItemList = dto => {
        // ...

        const validation = validateDTO(dto, 'view_item_list');
        if (!validation.valid) {
            validation.errors.forEach(error => console.error(error));
            return false;
        }

        try {
            pushEcommerceEvent('view_item_list', {
                value: dto.value,
                currency: dto.currency,
                items: dto.items,
            });
            return true;
        } catch (err) {
            console.error('[GA4Core] trackViewItemList - Error pushing to datalayer:', err);
            return false;
        }
    };

    /**
     * trackViewItem(dto)
     * Push view_item event to GA4
     */
    const trackViewItem = dto => {
        // ...

        const validation = validateDTO(dto, 'view_item');
        if (!validation.valid) {
            validation.errors.forEach(error => console.error(error));
            return false;
        }

        try {
            pushEcommerceEvent('view_item', {
                value: dto.value,
                currency: dto.currency,
                items: dto.items,
            });
            return true;
        } catch (err) {
            console.error('[GA4Core] trackViewItem - Error pushing to datalayer:', err);
            return false;
        }
    };

    /**
     * trackBeginCheckout(dto)
     * Push begin_checkout event to GA4
     */
    const trackBeginCheckout = dto => {
        // ...

        const validation = validateDTO(dto, 'begin_checkout');
        if (!validation.valid) {
            validation.errors.forEach(error => console.error(error));
            return false;
        }

        try {
            pushEcommerceEvent('begin_checkout', {
                value: dto.value,
                currency: dto.currency,
                items: dto.items,
            });
            return true;
        } catch (err) {
            console.error('[GA4Core] trackBeginCheckout - Error pushing to datalayer:', err);
            return false;
        }
    };

    /**
     * trackAddPaymentInfo(dto, paymentMethod)
     * Push add_payment_info event to GA4
     */
    const trackAddPaymentInfo = (dto, paymentMethod = '') => {
        // ...

        const validation = validateDTO(dto, 'add_payment_info');
        if (!validation.valid) {
            validation.errors.forEach(error => console.error(error));
            return false;
        }

        try {
            pushEcommerceEvent('add_payment_info', {
                value: dto.value,
                currency: dto.currency,
                payment_type: paymentMethod,
                items: dto.items,
            });
            return true;
        } catch (err) {
            console.error('[GA4Core] trackAddPaymentInfo - Error pushing to datalayer:', err);
            return false;
        }
    };

    /**
     * trackPurchase(dto, tpvOrder, sessionId, tipo) - ASYNC
     * Push purchase event to GA4 with backend guard
     *
     * Backend guard logic:
     * - Calls store.dispatch('TRACKED_GA', { tpv_order, session_id, tipo })
     * - If response.tracked === true → return false (already tracked)
     * - If error → return false (safety)
     * - Otherwise → push to GA4 and return true
     */
    const trackPurchase = async(dto, tpvOrder, sessionId, tipo) => {
        // ...

        const validation = validateDTO(dto, 'purchase');
        if (!validation.valid) {
            validation.errors.forEach(error => console.error(error));
            return false;
        }

        // Check in-memory guard for duplicate transactionId
        if (trackedTransactionIds.value.has(dto.transactionId)) {
            console.warn('[GA4Core] trackPurchase - Duplicate transactionId detected in memory:', dto.transactionId);
            return false;
        }

        try {
            const alias = getVuexAlias('tracked.ga');
            const res = await store.dispatch(alias, {
                tpv_order: tpvOrder,
                session_id: sessionId,
                tipo: tipo,
            });

            // If response indicates already tracked, return false
            if (res && res.data && (res.data.success === 0 || res.data.success === false)) {
                console.warn('[GA4Core] trackPurchase - Backend indicates already tracked, skipping GA4 push');
                return false;
            }

            // If no error and not already tracked, proceed to push
            pushEcommerceEvent('purchase', {
                transaction_id: dto.transactionId,
                value: dto.value,
                currency: dto.currency,
                items: dto.items,
            });

            // Add to in-memory tracking set
            trackedTransactionIds.value.add(dto.transactionId);

            return true;
        } catch (err) {
            console.error('[GA4Core] trackPurchase - Backend error or exception:', err);
            return false;
        }
    };

    /**
     * trackCustomEvent(name, params)
     * Push a custom (non-ecommerce) event to GA4 dataLayer.
     * No DTO validation — caller is responsible for correct payload shape.
     * @param {String} name - GA4 event name
     * @param {Object} params - Event parameters (optional)
     * @returns {Boolean} Success indicator
     */
    const trackCustomEvent = (name, params = {}) => {
        if (typeof window === 'undefined') {
            return false;
        }

        if (!name || typeof name !== 'string') {
            console.warn('[GA4Core] trackCustomEvent - name must be a non-empty string');
            return false;
        }

        try {
            ensureDataLayer();
            window.dataLayer.push({
                event: name,
                ...params,
            });
            return true;
        } catch (err) {
            console.error('[GA4Core] trackCustomEvent - Error pushing to datalayer:', err);
            return false;
        }
    };

    return {
        trackViewItemList,
        trackViewItem,
        trackBeginCheckout,
        trackAddPaymentInfo,
        trackPurchase,
        trackCustomEvent,
    };
}
